import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { getActiveToken } from '../utils/authStorage';
import {
  FaPlus,
  FaToggleOn,
  FaToggleOff,
  FaImage,
  FaCircle,
  FaTimes,
  FaSpinner,
  FaSync,
  FaUpload,
  FaFileImage,
  FaEye,
  FaCheck,
  FaTrash,
  FaCheckCircle,
  FaExclamationCircle,
  FaTimesCircle,
  FaTags,
  FaEdit
} from 'react-icons/fa';

const API_BASE = process.env.REACT_APP_REGIT_SERVICE_URL;

// --- Helpers pour l'auth (token) ---
const getToken = () => getActiveToken() || null;
// json = true => include Content-Type: application/json (useful pour GET/PUT/DELETE JSON)
// json = false => only Authorization (useful pour multipart/form-data)
const getAuthHeaders = (json = true) => {
  const token = getToken();
  if (!token) return null;
  return json
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { Authorization: `Bearer ${token}` };
};

// Composant de notification responsive
const Toast = ({ notification, onRemove }) => {
  const { id, type, message } = notification;
  
  useEffect(() => {
    const timer = setTimeout(() => {
      onRemove(id);
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [id, onRemove]);

  const getToastStyles = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-gradient-to-r from-green-500 to-green-600',
          icon: <FaCheckCircle className="text-white text-sm sm:text-lg" />,
          border: 'border-green-300'
        };
      case 'error':
        return {
          bg: 'bg-gradient-to-r from-red-500 to-red-600',
          icon: <FaTimesCircle className="text-white text-sm sm:text-lg" />,
          border: 'border-red-300'
        };
      case 'warning':
        return {
          bg: 'bg-gradient-to-r from-orange-500 to-orange-600',
          icon: <FaExclamationCircle className="text-white text-sm sm:text-lg" />,
          border: 'border-orange-300'
        };
      default:
        return {
          bg: 'bg-gradient-to-r from-blue-500 to-blue-600',
          icon: <FaCheckCircle className="text-white text-sm sm:text-lg" />,
          border: 'border-blue-300'
        };
    }
  };

  const styles = getToastStyles();

  return (
    <div className={`${styles.bg} ${styles.border} border backdrop-blur-lg rounded-xl shadow-2xl p-3 sm:p-4 mb-3 transform transition-all duration-300 ease-in-out animate-slide-in-right w-full max-w-xs sm:max-w-sm`}>
      <div className="flex items-center space-x-2 sm:space-x-3">
        <div className="flex-shrink-0">
          {styles.icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium text-xs sm:text-sm leading-relaxed break-words">{message}</p>
        </div>
        <button
          onClick={() => onRemove(id)}
          className="flex-shrink-0 text-white/80 hover:text-white p-1 hover:bg-white/10 rounded-lg transition-all"
        >
          <FaTimes className="text-xs" />
        </button>
      </div>
      
      {/* Barre de progression */}
      <div className="mt-2 w-full bg-white/20 rounded-full h-1 overflow-hidden">
        <div className="bg-white h-full rounded-full animate-progress-bar"></div>
      </div>
    </div>
  );
};

// Container des notifications responsive
const ToastContainer = ({ notifications, removeNotification }) => {
  if (notifications.length === 0) return null;

  return (
    <div className="fixed bottom-2 sm:bottom-4 right-2 sm:right-4 left-2 sm:left-auto z-[60] space-y-2">
      {notifications.map((notification) => (
        <Toast
          key={notification.id}
          notification={notification}
          onRemove={removeNotification}
        />
      ))}
    </div>
  );
};

export default function Images() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isUpdating, setIsUpdating] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [filename, setFilename] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [availableTags, setAvailableTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [selectedUploadTagIds, setSelectedUploadTagIds] = useState([]);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [editingTagIds, setEditingTagIds] = useState([]);
  const [savingTags, setSavingTags] = useState(false);
  const [tagFilter, setTagFilter] = useState('all');
  
  // État pour les notifications
  const [notifications, setNotifications] = useState([]);

  // Fonction pour ajouter une notification
  const addNotification = (type, message) => {
    const id = Date.now() + Math.random();
    const newNotification = { id, type, message };
    setNotifications(prev => [...prev, newNotification]);
  };

  // Fonction pour supprimer une notification
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  // Récupération des images
  const fetchImages = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = getAuthHeaders();
      if (!headers) {
        setError('Token manquant');
        addNotification('error', 'Token manquant — authentification requise');
        setLoading(false);
        return;
      }

      const res = await fetch(`${API_BASE}/regit`, { headers });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { images: [] }; }

      if (!res.ok) {
        const message = (data && data.message) || `Erreur récupération (${res.status})`;
        throw new Error(message);
      }

      setImages(Array.isArray(data.images) ? data.images : []);
    } catch (err) {
      setError(err.message);
      addNotification('error', `Erreur lors du chargement des images: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    setTagsLoading(true);
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const res = await fetch(`${API_BASE}/tags?active=true`, { headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || 'Erreur récupération tags');
      }
      setAvailableTags(Array.isArray(data?.tags) ? data.tags : []);
    } catch (err) {
      console.warn('Erreur fetchTags:', err);
      setAvailableTags([]);
    } finally {
      setTagsLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
    fetchTags();
   // eslint-disable-next-line

  }, []);

  // Toggle status d'une image
  const toggleStatus = async (id, currentStatus) => {
    setIsUpdating(id);
    try {
      const headers = getAuthHeaders();
      if (!headers) throw new Error('Token manquant');

      const res = await fetch(`${API_BASE}/regit/${id}/toggle`, {
        method: 'PUT',
        headers
      });

      const text = await res.text();
      let result;
      try { result = JSON.parse(text); } catch { result = { message: text }; }

      if (!res.ok) throw new Error(result.message || 'Erreur mise à jour statut');
      const updatedImage = result.image || {};

      // Mettre à jour la liste avec la nouvelle valeur
      setImages(prev => prev.map(img => (img.id === id ? { ...img, active: updatedImage.active } : img)));
      const statusText = updatedImage.active ? 'activée' : 'désactivée';
      addNotification('success', `Image ${statusText} avec succès`);
    } catch (err) {
      addNotification('error', `Erreur lors de la mise à jour: ${err.message}`);
    } finally {
      setIsUpdating(null);
    }
  };

  // Suppression d'une image
  const deleteImage = async (id, filenameToDelete) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${filenameToDelete}" ?`)) return;
    setDeleting(id);
    try {
      const headers = getAuthHeaders();
      if (!headers) throw new Error('Token manquant');

      const res = await fetch(`${API_BASE}/regit/${id}`, {
        method: 'DELETE',
        headers
      });

      const text = await res.text();
      let result;
      try { result = JSON.parse(text); } catch { result = { message: text }; }

      if (!res.ok) throw new Error(result.message || 'Erreur suppression');

      // Retirer l'image de la liste
      setImages(prev => prev.filter(img => img.id !== id));
      addNotification('success', `Image "${filenameToDelete}" supprimée avec succès`);
    } catch (err) {
      addNotification('error', `Erreur lors de la suppression: ${err.message}`);
    } finally {
      setDeleting(null);
    }
  };

  // Upload d'image
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      addNotification('warning', 'Veuillez sélectionner une image');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('image', selectedFile);
    formData.append('tagIds', JSON.stringify(selectedUploadTagIds));

    try {
      const authHeaders = getAuthHeaders(false); // no content-type for FormData
      if (!authHeaders) throw new Error('Token manquant');

      const res = await fetch(`${API_BASE}/regit/upload`, {
        method: 'POST',
        headers: authHeaders,
        body: formData
      });

      const text = await res.text();
      let result;
      try { result = JSON.parse(text); } catch { result = { message: text }; }

      if (!res.ok) throw new Error(result.message || 'Erreur upload');
      // Rafraîchir la liste
      await fetchImages();
      setShowForm(false);
      resetForm();
      addNotification('success', `Image "${result.image?.filename || 'upload'}" uploadée avec succès!`);
    } catch (err) {
      console.error('❌ Erreur upload:', err);
      addNotification('error', `Erreur lors de l'upload: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Gestion de la sélection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith('image/')) {
      addNotification('warning', 'Veuillez sélectionner un fichier image valide');
      return;
    }
    setSelectedFile(file);
    setFilename(file.name.replace(/\.[^/.]+$/, ''));
    
    const reader = new FileReader();
    reader.onload = ev => setPreviewUrl(ev.target.result);
    reader.readAsDataURL(file);
  };

  const resetForm = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setFilename('');
    setSelectedUploadTagIds([]);
    setShowForm(false);
  };

  const getTagName = (tagId) => {
    const found = availableTags.find((tag) => String(tag._id) === String(tagId));
    return found?.name || null;
  };

  const openEditTagsModal = (img) => {
    setEditingImage(img);
    setEditingTagIds(Array.isArray(img?.tags) ? img.tags.map((id) => String(id)) : []);
    setShowTagsModal(true);
  };

  const closeEditTagsModal = () => {
    if (savingTags) return;
    setShowTagsModal(false);
    setEditingImage(null);
    setEditingTagIds([]);
  };

  const toggleEditingTag = (tagId) => {
    setEditingTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const saveImageTags = async () => {
    if (!editingImage) return;
    setSavingTags(true);
    try {
      const headers = getAuthHeaders();
      if (!headers) throw new Error('Token manquant');

      const res = await fetch(`${API_BASE}/regit/${editingImage.id}/tags`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ tagIds: editingTagIds })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || 'Erreur mise à jour des tags');
      }

      const updatedTagIds = Array.isArray(data?.image?.tags)
        ? data.image.tags.map((id) => String(id))
        : editingTagIds;

      setImages((prev) =>
        prev.map((img) => (img.id === editingImage.id ? { ...img, tags: updatedTagIds } : img))
      );
      addNotification('success', 'Tags de l’image mis à jour');
      closeEditTagsModal();
    } catch (err) {
      addNotification('error', `Erreur tags: ${err.message}`);
    } finally {
      setSavingTags(false);
    }
  };

  const formatDate = dateString =>
    new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric'
    });

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
  };

  const handleRefresh = async () => {
    await fetchImages();
    addNotification('success', 'Images actualisées avec succès');
  };

  const filteredImages = useMemo(() => {
    if (tagFilter === 'all') return images;
    if (tagFilter === 'untagged') {
      return images.filter((img) => !Array.isArray(img.tags) || img.tags.length === 0);
    }
    return images.filter((img) =>
      Array.isArray(img.tags) && img.tags.some((id) => String(id) === String(tagFilter))
    );
  }, [images, tagFilter]);

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Sidebar />

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Navbar />

        <div className="shrink-0 border-b bg-white shadow-sm">
          <div className="p-3 sm:p-4 lg:p-6">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center space-x-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-r from-blue-600 to-blue-700">
                  <FaImage className="text-sm text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-gray-900 sm:text-xl">Publicité</h1>
                  <p className="text-xs text-gray-500 sm:text-sm">Gestion des images publicitaires</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={tagFilter}
                  onChange={(e) => setTagFilter(e.target.value)}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 sm:text-sm"
                >
                  <option value="all">Tous les tags</option>
                  <option value="untagged">Sans tag</option>
                  {availableTags.map((tag) => (
                    <option key={tag._id} value={String(tag._id)}>
                      {tag.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="flex items-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                >
                  <FaSync className={`mr-1.5 text-xs ${loading ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">Actualiser</span>
                </button>
                <button
                  onClick={() => setShowForm(true)}
                  className="flex items-center rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-3 py-2 text-xs font-medium text-white shadow-sm transition hover:from-blue-700 hover:to-blue-800 sm:text-sm"
                >
                  <FaUpload className="mr-1.5 text-xs" />
                  <span className="hidden sm:inline">Nouvelle image</span>
                  <span className="sm:hidden">Nouveau</span>
                </button>
              </div>
            </div>

            {/* Statistiques responsive */}
            <div className="grid grid-cols-3 gap-2">
              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50/90 px-2.5 py-2 shadow-sm ring-1 ring-slate-900/5">
                <FaImage className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500">Total</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-slate-900">{filteredImages.length}</p>
                </div>
              </div>
              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-emerald-200/70 bg-emerald-50/50 px-2.5 py-2 shadow-sm ring-1 ring-emerald-900/5">
                <FaCheck className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-emerald-700/90">Actives</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-emerald-900">{filteredImages.filter(img => img.active).length}</p>
                </div>
              </div>
              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-rose-200/70 bg-rose-50/50 px-2.5 py-2 shadow-sm ring-1 ring-rose-900/5">
                <FaTimes className="h-3.5 w-3.5 shrink-0 text-rose-600" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-rose-700/90">Inactives</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-rose-900">{filteredImages.filter(img => !img.active).length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal d'upload responsive */}
        {showForm && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-2 backdrop-blur-sm sm:p-4"
            onClick={() => setShowForm(false)}
          >
            <div
              className="max-h-[90vh] w-full max-w-sm overflow-y-auto rounded-2xl border border-slate-200/80 bg-white p-4 shadow-2xl shadow-slate-900/20 ring-1 ring-slate-900/5 sm:max-w-lg sm:p-8"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                    <FaUpload className="text-white text-xs sm:text-sm" />
                  </div>
                  <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Nouvelle Image</h2>
                </div>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-all"
                >
                  <FaTimes />
                </button>
              </div>
              
              <form onSubmit={handleUpload} className="space-y-4 sm:space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <FaTags className="inline mr-2" />
                    Tag (optionnel)
                  </label>
                  {tagsLoading ? (
                    <div className="text-sm text-gray-500 flex items-center border border-gray-200 rounded-xl p-3 bg-gray-50">
                      <FaSpinner className="animate-spin mr-2" />
                      Chargement des tags...
                    </div>
                  ) : availableTags.length === 0 ? (
                    <div className="text-sm text-gray-500 border border-gray-200 rounded-xl p-3 bg-gray-50">
                      Aucun tag actif
                    </div>
                  ) : (
                    <select
                      value={selectedUploadTagIds[0] || ''}
                      onChange={(e) => setSelectedUploadTagIds(e.target.value ? [e.target.value] : [])}
                      className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm bg-white"
                    >
                      <option value="">-- Sélectionner un tag --</option>
                      {availableTags.map((tag) => (
                        <option key={String(tag._id)} value={String(tag._id)}>
                          {tag.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <FaFileImage className="inline mr-2" />
                    Sélectionner une image *
                  </label>
                  <div className="mt-1 flex justify-center px-4 sm:px-6 pt-4 sm:pt-5 pb-4 sm:pb-6 border-2 border-gray-300 border-dashed rounded-xl hover:border-blue-400 transition-colors">
                    <div className="space-y-1 text-center">
                      {previewUrl ? (
                        <div className="mb-4">
                          <img
                            src={previewUrl}
                            alt="Prévisualisation"
                            className="mx-auto h-32 w-32 sm:h-40 sm:w-40 object-cover rounded-xl shadow-lg"
                          />
                        </div>
                      ) : (
                        <FaFileImage className="mx-auto h-8 w-8 sm:h-12 sm:w-12 text-gray-400" />
                      )}
                      <div className="flex text-sm text-gray-600">
                        <label className="relative cursor-pointer bg-white rounded-lg font-semibold text-blue-600 hover:text-blue-500 px-3 py-1">
                          <span>{selectedFile ? 'Changer l\'image' : 'Choisir un fichier'}</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="sr-only"
                            onChange={handleFileSelect}
                          />
                        </label>
                      </div>
                      <p className="text-xs text-gray-500">PNG, JPG, GIF jusqu'à 10MB</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="w-full sm:w-auto px-6 py-3 rounded-xl border border-gray-300 hover:bg-gray-50 transition-all font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={!selectedFile || uploading}
                    className="w-full sm:w-auto px-6 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {uploading ? (
                      <>
                        <FaSpinner className="mr-2 inline animate-spin" />
                        Upload en cours...
                      </>
                    ) : (
                      <>
                        <FaUpload className="mr-2 inline" />
                        Uploader
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <main className="min-h-0 flex-1 overflow-auto p-3 sm:p-4 lg:p-6">
          {loading && (
            <div className="flex h-64 items-center justify-center">
              <div className="text-center">
                <FaSpinner className="mx-auto mb-3 text-3xl animate-spin text-blue-600" />
                <p className="text-sm font-medium text-slate-600">Chargement des images…</p>
              </div>
            </div>
          )}

          {error && (
            <div className="mx-auto max-w-lg rounded-xl border border-rose-200 bg-rose-50/80 p-4 text-center shadow-sm ring-1 ring-rose-900/5 sm:p-6">
              <FaTimes className="mx-auto mb-2 text-xl text-rose-600 sm:text-2xl" />
              <p className="text-sm font-medium text-rose-800 sm:text-base">Erreur : {error}</p>
            </div>
          )}

          {!loading && !error && (
            <>
              {filteredImages.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-12 text-center shadow-sm ring-1 ring-slate-900/5 sm:py-16">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 sm:h-20 sm:w-20">
                    <FaImage className="text-2xl text-slate-400 sm:text-3xl" />
                  </div>
                  <h3 className="mb-2 text-lg font-semibold text-slate-800 sm:text-xl">Aucune image</h3>
                  <p className="mb-6 text-sm text-slate-500 sm:text-base">Commencez par ajouter votre première image</p>
                  <button
                    onClick={() => setShowForm(true)}
                    className="inline-flex items-center rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:from-blue-700 hover:to-blue-800 sm:px-6 sm:py-3"
                  >
                    <FaUpload className="mr-2" />
                    Ajouter une image
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8">
                  {filteredImages.map(img => (
                    <div
                      key={img.id}
                      className="group overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm ring-1 ring-slate-900/5 transition-all hover:border-slate-300/80 hover:shadow-md"
                    >
                      {/* Image responsive */}
                      <div className="relative aspect-square overflow-hidden">
                        <img
                          src={`${API_BASE}/regit/${img.id}`}
                          alt={img.filename}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          onError={(e) => {
                            e.target.src = '';
                            e.target.style.display = 'none';
                            const placeholder = e.target.nextSibling;
                            if (placeholder) placeholder.style.display = 'flex';
                          }}
                        />
                        <div
                          className="absolute inset-0 bg-gray-100 flex items-center justify-center hidden"
                          style={{ display: 'none' }}
                        >
                          <FaImage className="text-gray-400 text-lg sm:text-2xl" />
                        </div>
                        
                        <div className="absolute left-1.5 top-1.5 sm:left-2 sm:top-2">
                          {img.active ? (
                            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 ring-1 ring-emerald-200/70 sm:h-auto sm:w-auto sm:px-1.5 sm:py-0.5">
                              <FaCircle className="h-1.5 w-1.5 text-emerald-600" />
                              <span className="hidden sm:ml-0.5 sm:inline text-[10px] font-semibold text-emerald-800">Actif</span>
                            </span>
                          ) : (
                            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-rose-100 ring-1 ring-rose-200/70 sm:h-auto sm:w-auto sm:px-1.5 sm:py-0.5">
                              <FaCircle className="h-1.5 w-1.5 text-rose-600" />
                              <span className="hidden sm:ml-0.5 sm:inline text-[10px] font-semibold text-rose-800">Inactif</span>
                            </span>
                          )}
                        </div>

                        <div className="absolute right-1.5 top-1.5 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 sm:right-2 sm:top-2">
                          <button
                            onClick={() => window.open(`${API_BASE}/regit/${img.id}`, '_blank')}
                            className="flex h-6 w-6 items-center justify-center rounded-full bg-white/95 text-slate-600 shadow-sm ring-1 ring-slate-200/80 transition hover:text-blue-600 sm:h-7 sm:w-7"
                          >
                            <FaEye className="text-xs" />
                          </button>
                          <button
                            onClick={() => deleteImage(img.id, img.filename)}
                            disabled={deleting === img.id}
                            className="flex h-6 w-6 items-center justify-center rounded-full bg-white/95 text-slate-600 shadow-sm ring-1 ring-slate-200/80 transition hover:text-rose-600 sm:h-7 sm:w-7"
                          >
                            {deleting === img.id ? (
                              <FaSpinner className="text-xs animate-spin" />
                            ) : (
                              <FaTrash className="text-xs" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="border-t border-slate-100 bg-slate-50/40 p-2 sm:p-2.5">
                        <div className="mb-2">
                          <div className="flex flex-wrap gap-1">
                            {Array.isArray(img.tags) && img.tags.length > 0 ? (
                              img.tags.slice(0, 2).map((tagId) => {
                                const tagName = getTagName(tagId);
                                return (
                                  <span key={`${img.id}-${tagId}`} className="inline-flex items-center rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700 ring-1 ring-indigo-200/60">
                                    {tagName || 'Tag'}
                                  </span>
                                );
                              })
                            ) : (
                              <span className="text-[10px] text-slate-400">Aucun tag</span>
                            )}
                            {Array.isArray(img.tags) && img.tags.length > 2 && (
                              <span className="text-[10px] text-slate-500">+{img.tags.length - 2}</span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => openEditTagsModal(img)}
                            className="rounded-lg border border-blue-200/70 bg-blue-50/60 px-2 py-1 text-[10px] font-semibold text-blue-700 transition hover:bg-blue-100/80 sm:text-xs"
                          >
                            <FaEdit className="mr-1 inline" />
                            Tags
                          </button>
                          <button
                            onClick={() => toggleStatus(img.id, img.active)}
                            disabled={isUpdating === img.id}
                            className={`flex-1 rounded-lg border px-2 py-1 text-[10px] font-semibold transition sm:text-xs ${
                              img.active
                                ? 'border-rose-200/70 bg-rose-50/60 text-rose-700 hover:bg-rose-100/80'
                                : 'border-emerald-200/70 bg-emerald-50/60 text-emerald-700 hover:bg-emerald-100/80'
                            }`}
                          >
                            {isUpdating === img.id ? (
                              <FaSpinner className="animate-spin mx-auto" />
                            ) : (
                              <>
                                <span className="hidden sm:inline">
                                  {img.active ? 'Désactiver' : 'Activer'}
                                </span>
                                <span className="sm:hidden">
                                  {img.active ? 'Désactiver' : 'Activer'}
                                </span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {showTagsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-2 backdrop-blur-sm sm:p-4"
          onClick={closeEditTagsModal}
        >
          <div
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-slate-200/80 bg-white p-4 shadow-2xl shadow-slate-900/20 ring-1 ring-slate-900/5 sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600 ring-1 ring-blue-200/60">
                  <FaTags className="text-sm" />
                </div>
                <h2 className="text-lg font-bold text-slate-900 sm:text-xl">
                  Tags de l'image
                </h2>
              </div>
              <button
                onClick={closeEditTagsModal}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-all"
              >
                <FaTimes />
              </button>
            </div>

            <p className="mb-3 break-all rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600 ring-1 ring-slate-200/60">
              {editingImage?.filename || 'Image'}
            </p>

            <div className="max-h-64 overflow-y-auto rounded-xl border border-slate-200/80 bg-slate-50/40 p-2 ring-1 ring-slate-900/5">
              {tagsLoading ? (
                <div className="text-sm text-gray-500 flex items-center">
                  <FaSpinner className="animate-spin mr-2" />
                  Chargement des tags...
                </div>
              ) : availableTags.length === 0 ? (
                <p className="text-sm text-gray-500">Aucun tag actif</p>
              ) : (
                <div className="space-y-2">
                  {availableTags.map((tag) => {
                    const id = String(tag._id);
                    const checked = editingTagIds.includes(id);
                    return (
                      <label key={id} className="flex cursor-pointer items-center justify-between rounded-lg border border-transparent p-2 transition hover:border-slate-200 hover:bg-white">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{tag.name}</p>
                          <p className="text-xs text-gray-500">{tag.slug || '—'}</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleEditingTag(id)}
                          className="w-4 h-4"
                        />
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={closeEditTagsModal}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={saveImageTags}
                disabled={savingTags}
                className="rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:from-blue-700 hover:to-blue-800 disabled:opacity-50"
              >
                {savingTags ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Container des notifications responsive */}
      <ToastContainer 
        notifications={notifications} 
        removeNotification={removeNotification} 
      />

      {/* Styles CSS pour les animations */}
      <style jsx>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes progress-bar {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
        
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
        
        .animate-progress-bar {
          animation: progress-bar 2s linear;
        }
      `}</style>
    </div>
  );
}
